<template>
  <BaseDialog max-width="800" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="modalTitle" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <v-progress-linear
            v-if="isSubmiting || loading"
            class="progression"
            indeterminate
          />
          <BaseTextField
            v-model="form.name"
            :label="$tc('utils.name', 1) + '*'"
            :error-messages="nameErrors"
            :has-error="!!nameErrors"
            @change="$v.form.$touch()"
          />
          <BaseFileDrop
            v-model="form.file"
            accept=".doc,.docx,.xls,.xlsx,.csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            :error-messages="fileErrors"
            :has-error="!!fileErrors"
            @changeValue="$v.form.$touch()"
          />
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton color="primary" type="primary" @click="submit()">
            {{ docxTemplate ? $t("utils.edit") : $t("utils.import") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import OrganizationAPI from "@/core/Services/organization.api";
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import { DocxTemplate } from "@/core/Models/DocxTemplate";
import { fileURLToFile } from "@/core/Utils/file.utils";

export default {
  name: "DocxTemplateImportDialog",
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    docxTemplate: {
      type: DocxTemplate,
      default: () => null,
    },
  },
  data() {
    return {
      loading: false,
      isSubmiting: false,
      form: {
        file: null,
        name: "",
      },
    };
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(255),
      },
      file: {
        required,
      },
    },
  },
  computed: {
    modalTitle() {
      return this.docxTemplate
        ? this.$t("utils.editing_docx_template")
        : this.$t("utils.adding_docx_template");
    },
    nameErrors() {
      if (!this.$v.form.name.$dirty) return "";
      if (!this.$v.form.name.required)
        return this.$t("folder.createAndEditForm.validation.nameRequired");
      if (!this.$v.form.name.maxLength)
        return this.$t("folder.createAndEditForm.validation.nameTooLong");
      return "";
    },
    fileErrors() {
      if (!this.$v.form.file.$dirty) return "";
      if (!this.$v.form.file.required) return this.$t("utils.fileRequired");
      return "";
    },
  },
  created() {
    this.initForm();
  },
  methods: {
    async initForm() {
      if (!this.docxTemplate) return;
      this.loading = true;
      this.form.name = this.docxTemplate.name;
      this.form.file = await fileURLToFile(
        this.docxTemplate.file,
        this.docxTemplate.name
      );
      this.loading = false;
    },
    reset() {
      if (this.isSubmiting) return;
      this.form.file = null;
      this.form.name = null;
      this.$v.form.$reset();
      this.$emit("close");
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid || this.loading || this.isSubmiting) return;
      this.isSubmiting = true;
      try {
        if (this.docxTemplate) {
          await this.editDocxTemplate();
        } else {
          await this.createDocxTemplate();
        }
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.reset();
    },
    async createDocxTemplate() {
      await OrganizationAPI.postOrganizationDocxTemplate({
        organizationId: this.organizationId,
        file: this.form.file,
        name: this.form.name,
      });
      this.$store.dispatch("snackbar/active", {
        message: this.$t("utils.imported_successfully"),
        type: "SUCCESS",
      });
      this.$emit("fetch-docx-templates");
    },
    async editDocxTemplate() {
      const res = await OrganizationAPI.patchOrganizationDocxTemplate({
        organizationId: this.organizationId,
        docxTemplateId: this.docxTemplate.id,
        file: this.form.file,
        name: this.form.name,
      });
      this.$store.dispatch("snackbar/active", {
        message: this.$t("snackbar.editSuccess"),
        type: "SUCCESS",
      });
      this.$emit("docx-templates-edited", {
        ...this.docxTemplate,
        name: res.data.name,
        file: res.data.file,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  position: relative;
  .progression {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 10;
  }
  .notif {
    font-family: "Inter";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 171.52%;
    text-align: center;
    color: #2536cc;
    padding: 0 16px;
    margin-bottom: 24px;
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  margin-top: 16px;
  padding: 8px 0;
  gap: 16px;
}
</style>
